if (!customElements.get('product-variants-form')) {
  customElements.define('product-variants-form', class ProductVariantsForm extends HTMLElement {
    constructor() {
      super();
      
      this.submitButton = this.querySelector('.product-variants__submit');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.quantityInput = this.querySelector('.product-variants__quantity-input');
      this.variantInput = this.querySelector('.product-variant-input[name="id"]');
      this.colorNameDisplay = this.querySelector('[data-selected-color-name]');
      this.variantsData = this.parseVariantsData();
      this.selectedColor = this.variantsData?.selectedColor || null;
      this.selectedSize = this.variantsData?.selectedSize || null;
      
      this.init();
    }

    init() {
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
      }

      const colorSwatches = this.querySelectorAll('.product-variants__color-swatch');
      colorSwatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => this.onColorSelect(e));
      });

      const sizeOptions = this.querySelectorAll('.product-variants__size-option');
      sizeOptions.forEach(option => {
        option.addEventListener('click', (e) => this.onSizeSelect(e));
      });

      const variantInputs = this.querySelectorAll('.product-variant-input[type="radio"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.updateProductInfo(input);
        });
      });

      const decreaseBtn = this.querySelector('.product-variants__quantity-decrease');
      const increaseBtn = this.querySelector('.product-variants__quantity-increase');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => this.changeQuantity(-1));
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => this.changeQuantity(1));
      }

      const variants = this.variantsData?.variants;
      if (variants?.length && variants.every((variant) => !variant.available)) {
        this.updateSubmitButton(false, false);
      }
    }

    parseVariantsData() {
      const dataScript = this.querySelector('[data-variants-json]');
      if (!dataScript) return null;
      
      try {
        return JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Error parsing variants data:', e);
        return null;
      }
    }

    onColorSelect(event) {
      const swatch = event.currentTarget;
      
      if (swatch.classList.contains('product-variants__color-swatch--unavailable')) {
        return;
      }
      
      const colorValue = swatch.dataset.colorValue;
      const colorImage = swatch.dataset.colorImage;
      
      this.querySelectorAll('.product-variants__color-swatch').forEach(s => {
        s.classList.remove('product-variants__color-swatch--selected');
      });
      swatch.classList.add('product-variants__color-swatch--selected');
      
      this.selectedColor = colorValue;
      
      if (this.colorNameDisplay) {
        this.colorNameDisplay.textContent = colorValue;
      }
      
      this.updateSizeAvailability();
      
      this.updateSelectedVariant();
      
      if (colorImage) {
        this.updateMainImage(colorImage);
      } else {
        const variant = this.findVariant(this.selectedColor, this.selectedSize);
        if (variant && variant.featured_image) {
          this.updateMainImage(variant.featured_image);
        }
      }
      
      this.dispatchEvent(new CustomEvent('color:change', {
        bubbles: true,
        detail: { 
          color: colorValue,
          colorImage: colorImage
        }
      }));
    }

    onSizeSelect(event) {
      const option = event.currentTarget;
      
      if (option.classList.contains('product-variants__size-option--unavailable')) {
        return;
      }
      
      const sizeValue = option.dataset.sizeValue;
      
      this.querySelectorAll('.product-variants__size-option').forEach(o => {
        o.classList.remove('product-variants__size-option--selected');
      });
      option.classList.add('product-variants__size-option--selected');
      this.selectedSize = sizeValue;
      
      this.updateSelectedVariant();
      
      this.dispatchEvent(new CustomEvent('size:change', {
        bubbles: true,
        detail: { 
          size: sizeValue
        }
      }));
    }

    updateSizeAvailability() {
      if (!this.variantsData) return;
      
      const { variants, colorOptionPosition, sizeOptionPosition } = this.variantsData;
      
      if (colorOptionPosition === null || sizeOptionPosition === null) return;
      
      const colorOptionKey = `option${colorOptionPosition}`;
      const sizeOptionKey = `option${sizeOptionPosition}`;
      
      const sizeOptions = this.querySelectorAll('.product-variants__size-option');
      
      sizeOptions.forEach(option => {
        const sizeValue = option.dataset.sizeValue;
        
        const isAvailable = variants.some(variant => 
          variant[colorOptionKey] === this.selectedColor && 
          variant[sizeOptionKey] === sizeValue && 
          variant.available
        );
        
        if (isAvailable) {
          option.classList.remove('product-variants__size-option--unavailable');
          option.removeAttribute('aria-disabled');
        } else {
          option.classList.add('product-variants__size-option--unavailable');
          option.setAttribute('aria-disabled', 'true');
          
          if (option.classList.contains('product-variants__size-option--selected')) {
            option.classList.remove('product-variants__size-option--selected');
            this.selectedSize = null;
          }
        }
      });
    }

    findVariant(color, size) {
      if (!this.variantsData) return null;
      
      const { variants, colorOptionPosition, sizeOptionPosition } = this.variantsData;
      
      const colorOptionKey = colorOptionPosition !== null ? `option${colorOptionPosition}` : null;
      const sizeOptionKey = sizeOptionPosition !== null ? `option${sizeOptionPosition}` : null;
      
      return variants.find(variant => {
        let matches = true;
        
        if (colorOptionKey) {
          matches = matches && color && variant[colorOptionKey] === color;
        }
        
        if (sizeOptionKey) {
          matches = matches && size && variant[sizeOptionKey] === size;
        }
        
        return matches;
      });
    }

    hasCompleteSelection() {
      if (!this.variantsData?.requiresSelection) return true;

      const { colorOptionPosition, sizeOptionPosition } = this.variantsData;

      if (colorOptionPosition !== null && !this.selectedColor) return false;
      if (sizeOptionPosition !== null && !this.selectedSize) return false;

      return true;
    }

    updateSelectedVariant() {
      const variant = this.findVariant(this.selectedColor, this.selectedSize);
      
      if (!variant) {
        if (this.variantInput) {
          this.variantInput.value = '';
          this.variantInput.dataset.available = 'false';
        }

        this.updateSubmitButton(false, !this.hasCompleteSelection());
        return;
      }
      
      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.dataset.available = variant.available;
      }
      
      this.updateSubmitButton(variant.available);
      
      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { 
          variantId: variant.id,
          price: variant.price,
          comparePrice: variant.compare_at_price,
          available: variant.available,
          variant: variant
        }
      }));
    }

    updateSubmitButton(available, missingSelection = false) {
      if (!this.submitButton) return;
      
      const submitText = this.submitButton.querySelector('.product-variants__submit-text');
      
      if (available) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('disabled');
        if (submitText) {
          submitText.textContent = 'ADICIONAR PRODUTO AO CARRINHO';
        }
      } else {
        this.submitButton.setAttribute('disabled', 'true');
        this.submitButton.classList.add('disabled');
        if (submitText) {
          submitText.textContent = missingSelection ? 'SELECIONE AS OPÇÕES' : 'PRODUTO ESGOTADO';
        }
      }
    }

    updateMainImage(imageSrc) {
      const mainImage = document.querySelector('.product__media-item--active img, .product-gallery__main img, [data-product-main-image]');
      
      if (mainImage) {
        mainImage.src = imageSrc;
        mainImage.srcset = imageSrc;
      }
      
      this.dispatchEvent(new CustomEvent('image:change', {
        bubbles: true,
        detail: { 
          imageSrc: imageSrc
        }
      }));
    }

    changeQuantity(change) {
      if (!this.quantityInput) return;
      
      const currentValue = parseInt(this.quantityInput.value) || 1;
      const newValue = Math.max(1, currentValue + change);
      this.quantityInput.value = newValue;
    }

    updateProductInfo(input) {
      const price = input.dataset.price;
      const comparePrice = input.dataset.comparePrice;
      const available = input.dataset.available === 'true';

      this.updateSubmitButton(available);

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { 
          variantId: input.value,
          price: price,
          comparePrice: comparePrice,
          available: available
        }
      }));
    }

    onSubmitHandler(event) {
      event.preventDefault();
      
      if (!this.cartDrawer) {
        console.warn('Cart drawer não encontrado');
        return;
      }
      
      if (this.submitButton.classList.contains('is-loading') || 
          this.submitButton.hasAttribute('disabled')) {
        return;
      }

      this.handleErrorMessage();
      this.cartDrawer.setActiveElement(document.activeElement);

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('is-loading');
      
      const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const fetchedData = this.addProductToCart();
      this.handleFetchedData(fetchedData);
    }

    getSelectedVariant() {
      if (this.variantInput && this.variantInput.value) {
        const quantity = this.quantityInput ? parseInt(this.quantityInput.value) || 1 : 1;
        return {
          id: this.variantInput.value,
          quantity: quantity
        };
      }
      
      const selectedInput = this.querySelector('.product-variant-input:checked');
      
      if (!selectedInput || !selectedInput.value || selectedInput.disabled) {
        return null;
      }

      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) || 1 : 1;

      return {
        id: selectedInput.value,
        quantity: quantity
      };
    }

    addProductToCart() {
      return new Promise((resolve, reject) => {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        const variant = this.getSelectedVariant();
        
        if (!variant) {
          reject({ 
            status: true, 
            description: 'Por favor, selecione uma variante do produto.' 
          });
          return;
        }

        const body = {
          items: [variant],
          sections: this.cartDrawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        };

        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status || response.message) {
              reject(response);
              return;
            }
            resolve(response);
          })
          .catch((error) => {
            console.error('Erro ao adicionar produto ao carrinho:', error);
            reject(error);
          });
      });
    }

    handleFetchedData(promise) {
      Promise.all([promise])
        .then((values) => {
          if (values.length === 0) return;

          const fetchedData = values[0];

          if (fetchedData.status) {
            this.handleErrorMessage(fetchedData.description || fetchedData.message);
            return;
          }

          this.cartDrawer.handleAddedItem(fetchedData);
        })
        .catch((error) => {
          console.error('Erro:', error);
          const errorMsg = error.description || 
                          error.message || 
                          'Erro ao adicionar produto ao carrinho. Tente novamente.';
          this.handleErrorMessage(errorMsg);
        })
        .finally(() => {
          this.submitButton.classList.remove('is-loading');
          this.submitButton.removeAttribute('aria-disabled');
          
          const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || 
                                  this.querySelector('.product-variants__error-message-wrapper');
      this.errorMessage = this.errorMessage || 
                          this.errorMessageWrapper?.querySelector('.product-variants__error-message');

      if (!this.errorMessageWrapper || !this.errorMessage) return;

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
